/**
 * Global utility for working with component constants
 * Provides a consistent way to access constants across all components
 */

/**
 * Get constant value with fallback to default
 * @param constants - Component constants object
 * @param key - Constant key to retrieve
 * @param defaultValue - Fallback value if constant not found
 * @param element - Optional HTMLElement to read constants from if constants prop is not available (during hydration)
 * @returns Constant value or default value
 */
export function getConstantValue(constants: Record<string, string> | string | undefined, key: string, defaultValue: string, element?: HTMLElement): string {
  const obj = normalizeConstants(constants, element);
  return obj?.[key] || defaultValue;
}

/**
 * Get constant value with fallback to default
 * @param constants - Component constants object
 * @param key - Constant key to retrieve
 * @param defaultValue - Fallback value if constant not found
 * @param element - Optional HTMLElement to read constants from if constants prop is not available (during hydration)
 * @returns Constant value or default value
 */
export const getConstant = getConstantValue;

/**
 * Get multiple constants at once
 * @param constants - Component constants object
 * @param keys - Object with key-defaultValue pairs
 * @param element - Optional HTMLElement to read constants from if constants prop is not available (during hydration)
 * @returns Object with resolved constant values
 */
export function getMultipleConstants(constants: Record<string, string> | string | undefined, keys: Record<string, string>, element?: HTMLElement): Record<string, string> {
  const result: Record<string, string> = {};
  const obj = normalizeConstants(constants, element);
  Object.entries(keys).forEach(([key, defaultValue]) => {
    result[key] = (obj?.[key] as string) || defaultValue;
  });

  return result;
}

/**
 * Check if constant exists
 * @param constants - Component constants object
 * @param key - Constant key to check
 * @param element - Optional HTMLElement to read constants from if constants prop is not available (during hydration)
 * @returns True if constant exists and has value
 */
export function hasConstant(constants: Record<string, string> | string | undefined, key: string, element?: HTMLElement): boolean {
  const obj = normalizeConstants(constants, element);
  return Boolean(obj?.[key]);
}

/**
 * Normalize constants prop to an object.
 * Accepts object or JSON string (with potential &quot; entities) and returns a plain object.
 * If constants is not provided, attempts to read from element's 'constants' attribute (for hydration support).
 * @param constants - Component constants object or JSON string
 * @param element - Optional HTMLElement to read constants from if constants prop is not available
 * @returns Normalized constants object or undefined
 */
function normalizeConstants(constants: Record<string, string> | string | undefined, element?: HTMLElement): Record<string, string> | undefined {
  // If constants is provided, normalize it
  if (constants) {
    if (typeof constants === 'object') {
      return constants as Record<string, string>;
    }
    if (typeof constants === 'string') {
      try {
        const jsonString = constants.includes('&quot;') ? constants.replace(/&quot;/g, '"') : constants;
        const parsed = JSON.parse(jsonString);
        return parsed && typeof parsed === 'object' ? (parsed as Record<string, string>) : undefined;
      } catch {
        return undefined;
      }
    }
  }

  // If constants is not available and element is provided, try to read from HTML attribute
  // This handles the case when Stencil doesn't parse constants during hydration
  if (!constants && element) {
    const constantsAttr = element.getAttribute('constants');
    if (constantsAttr) {
      try {
        const decoded = constantsAttr.replace(/&quot;/g, '"');
        const parsed = JSON.parse(decoded);
        return parsed && typeof parsed === 'object' ? (parsed as Record<string, string>) : undefined;
      } catch {
        return undefined;
      }
    }
  }

  return undefined;
}
